import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams, useParams } from "react-router-dom";
import { getErrorMessage } from "@/lib/errorHandler";
import API from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Mail, RefreshCw, AlertTriangle, ShieldCheck, ChevronLeft } from "lucide-react";
import phyberLogo from "@/assets/images/phyber-wordmark.png";
import { OtpBoxInput } from "@/components/ui/OtpBoxInput";

// ── Inject styles once ────────────────────────────────────────────────────────
const LOGIN_CSS = `

  .login-root {
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  }
  .login-heading {
    font-family: 'Lexend', sans-serif;
    font-weight: 600;
    letter-spacing: -0.03em;
  }

  /* Same dot-grid texture as QuizTaker for consistency */
  .login-bg {
    background-color: #f8fafc;
    background-image: radial-gradient(circle, #cbd5e1 1px, transparent 1px);
    background-size: 24px 24px;
  }

  /* Card */
  .login-card {
    background: white;
    border: 1px solid rgb(226 232 240);
    border-radius: 20px;
    box-shadow: 0 8px 32px 0 rgb(79 70 229 / 0.08), 0 1px 3px 0 rgb(0 0 0 / 0.06);
    overflow: hidden;
  }

  /* Top accent strip */
  .login-card-accent {
    height: 4px;
    background: linear-gradient(90deg, #6366f1 0%, #4f46e5 50%, #818cf8 100%);
  }

  /* Indigo focus ring for inputs */
  .login-root input:focus {
    outline: none;
    border-color: #4f46e5 !important;
    box-shadow: 0 0 0 3px rgb(99 102 241 / 0.15) !important;
  }

  /* Primary button */
  .login-btn {
    width: 100%;
    height: 48px;
    background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%);
    color: white;
    font-weight: 600;
    font-size: 15px;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s;
    box-shadow: 0 4px 14px 0 rgb(79 70 229 / 0.35);
    font-family: 'DM Sans', sans-serif;
  }
  .login-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px 0 rgb(79 70 229 / 0.4);
  }
  .login-btn:active:not(:disabled) { transform: translateY(0); }
  .login-btn:disabled { opacity: 0.55; cursor: not-allowed; }

  /* Email input field */
  .login-input {
    width: 100%;
    height: 48px;
    border: 1.5px solid rgb(226 232 240);
    border-radius: 12px;
    padding: 0 16px 0 44px;
    font-size: 15px;
    color: rgb(15 23 42);
    background: white;
    transition: border-color 0.15s, box-shadow 0.15s;
    font-family: 'DM Sans', sans-serif;
    box-sizing: border-box;
  }
  .login-input::placeholder { color: rgb(148 163 184); }

  /* Resend countdown ring */
  @keyframes loginFadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .login-fade-up   { animation: loginFadeUp 0.45s ease both; }
  .login-fade-up-d { animation: loginFadeUp 0.45s ease 0.06s both; }

  @keyframes slideLeft {
    from { opacity: 0; transform: translateX(20px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .login-slide-left { animation: slideLeft 0.35s ease both; }

  /* Spinner */
  @keyframes loginSpin { to { transform: rotate(360deg); } }
  .login-spinner {
    width: 18px; height: 18px;
    border: 2px solid rgba(255,255,255,0.35);
    border-top-color: white;
    border-radius: 50%;
    animation: loginSpin 0.7s linear infinite;
    flex-shrink: 0;
  }
`;

if (typeof document !== "undefined" && !document.getElementById("login-styles")) {
  const s = document.createElement("style");
  s.id = "login-styles";
  s.textContent = LOGIN_CSS;
  document.head.appendChild(s);
}

// ── Resend timer ring ─────────────────────────────────────────────────────────
function ResendTimer({ seconds, onResend, loading }) {
  const pct = seconds > 0 ? (60 - seconds) / 60 : 1;
  const r = 10;
  const circ = 2 * Math.PI * r;

  return (
    <div className="flex items-center justify-between pt-1">
      <button
        type="button"
        onClick={onResend}
        disabled={seconds > 0 || loading}
        className={`flex items-center gap-2 text-sm font-medium transition-colors ${
          seconds > 0 || loading
            ? "text-slate-400 cursor-not-allowed"
            : "text-indigo-600 hover:text-indigo-700"
        }`}
      >
        {seconds > 0 ? (
          <>
            <svg width="22" height="22" viewBox="0 0 22 22" className="-rotate-90">
              <circle cx="11" cy="11" r={r} stroke="#e2e8f0" strokeWidth="2" fill="none" />
              <circle
                cx="11" cy="11" r={r}
                stroke="#6366f1" strokeWidth="2" fill="none"
                strokeLinecap="round"
                strokeDasharray={circ}
                strokeDashoffset={circ * (1 - pct)}
                style={{ transition: "stroke-dashoffset 1s linear" }}
              />
            </svg>
            Resend in {seconds}s
          </>
        ) : (
          <>
            <RefreshCw className="w-3.5 h-3.5" />
            Resend OTP
          </>
        )}
      </button>
      <span className="text-xs text-slate-400">Check spam if not received</span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Login() {
  const { sendOtp, verifyOtp } = useAuth();
  const [searchParams] = useSearchParams();
  const { orgSlug: urlOrgSlug } = useParams();
  const [step, setStep] = useState("email"); // email | otp
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [orgBranding, setOrgBranding] = useState(null);

  useEffect(() => {
    const orgSlug = urlOrgSlug || searchParams.get("org");
    if (orgSlug) {
      API.get(`/superadmin/organizations/public/by-slug/${orgSlug}`)
        .then(res => setOrgBranding({ name: res.data.name, logo_url: res.data.logo_url, slug: res.data.slug }))
        .catch(() => {});
    }
  }, [urlOrgSlug, searchParams]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setInterval(() => setResendTimer(p => p - 1), 1000);
    return () => clearInterval(t);
  }, [resendTimer]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");
    try {
      await sendOtp(email);
      toast.success("OTP sent — check your inbox.");
      setStep("otp");
      setResendTimer(60);
    } catch (err) {
      const msg = getErrorMessage(err, "Something went wrong");
      setErrorMessage(msg);
      if (msg.includes("not found") || msg.includes("does not exist")) {
        toast.error("Account not found. Contact your administrator.");
      } else {
        toast.error(msg);
      }
    }
    setLoading(false);
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      await sendOtp(email);
      setResendTimer(60);
      setOtp("");
      toast.success("OTP resent.");
    } catch (err) {
      const msg = getErrorMessage(err, "Failed to resend OTP");
      setErrorMessage(msg);
      toast.error(msg);
    }
    setLoading(false);
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");
    try {
      await verifyOtp(email, otp);
      toast.success("Welcome!");
    } catch (err) {
      const msg = getErrorMessage(err, err.message || "Invalid OTP");
      setErrorMessage(msg);
      toast.error(msg, { duration: msg.includes("locked") ? 5000 : 3000 });
    }
    setLoading(false);
  };

  const brandLogo = orgBranding?.logo_url || phyberLogo;
  const brandName = orgBranding?.name || "Phyber";
  const isLocked = errorMessage && errorMessage.includes("locked");

  return (
    <div className="min-h-screen login-bg login-root flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">

        {/* Logo / branding */}
        <div className="text-center mb-8 login-fade-up">
          <div className="flex justify-center mb-4">
            <img src={brandLogo} alt={brandName} className="h-11 w-auto max-w-[220px] object-contain drop-shadow-sm" />
          </div>
          {!orgBranding && (
            <p className="text-sm text-slate-500 font-medium">Quiz &amp; Assessment Platform</p>
          )}
          {orgBranding && (
            <p className="text-sm text-slate-500 font-medium">{brandName} · Assessments</p>
          )}
        </div>

        {/* Card */}
        <div className="login-card login-fade-up-d">
          <div className="login-card-accent" />

          <div className="px-8 py-8">

            {/* ── STEP: Email ── */}
            {step === "email" && (
              <form onSubmit={handleEmailSubmit} className="login-slide-left">
                <div className="mb-6">
                  <h1 className="login-heading text-2xl text-slate-900 mb-1">Sign in</h1>
                  <p className="text-sm text-slate-500">Enter your work email to receive a one-time code</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                      Email address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        id="email"
                        type="email"
                        data-testid="login-email-input"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="name@company.com"
                        className="login-input"
                        required
                        autoFocus
                        autoComplete="email"
                      />
                    </div>
                    {errorMessage && !errorMessage.includes("locked") && (
                      <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {errorMessage}
                      </p>
                    )}
                  </div>

                  <button type="submit" data-testid="login-submit-btn" disabled={loading} className="login-btn">
                    {loading ? <span className="login-spinner" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </div>

                <p className="text-xs text-slate-400 text-center mt-5">
                  No account? Contact your administrator.
                </p>
              </form>
            )}

            {/* ── STEP: OTP ── */}
            {step === "otp" && (
              <form onSubmit={handleOtpVerify} className="login-slide-left">
                {/* Back + heading */}
                <button
                  type="button"
                  onClick={() => { setStep("email"); setOtp(""); setErrorMessage(""); setResendTimer(0); }}
                  data-testid="back-to-email-btn"
                  className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 mb-5 transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> {email}
                </button>

                <div className="mb-6">
                  <h1 className="login-heading text-2xl text-slate-900 mb-1">Check your inbox</h1>
                  <p className="text-sm text-slate-500">
                    We sent a 6-digit code to <span className="font-medium text-slate-700">{email}</span>
                  </p>
                </div>

                {/* Error banners */}
                {isLocked && (
                  <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl p-3.5 mb-5">
                    <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-700">{errorMessage}</p>
                  </div>
                )}
                {errorMessage && errorMessage.includes("attempt") && !isLocked && (
                  <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl p-3.5 mb-5">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-700">{errorMessage}</p>
                  </div>
                )}

                {/* 6-box OTP */}
                <div className="mb-5">
                  <OtpBoxInput value={otp} onChange={setOtp} disabled={loading || isLocked} />
                  {/* hidden for test compatibility */}
                  <input type="hidden" data-testid="otp-input" value={otp} readOnly />
                </div>

                <button
                  type="submit"
                  data-testid="verify-otp-btn"
                  disabled={loading || otp.length < 6 || isLocked}
                  className="login-btn mb-4"
                >
                  {loading
                    ? <span className="login-spinner" />
                    : <><ShieldCheck className="w-4 h-4" /> Verify &amp; Sign In</>
                  }
                </button>

                <ResendTimer seconds={resendTimer} onResend={handleResendOTP} loading={loading} />

                <p className="text-xs text-slate-500 text-center mt-4 leading-relaxed">
                  Didn't get the code? Check your spam folder, or{" "}
                  <button
                    type="button"
                    onClick={() => { setStep("email"); setOtp(""); setErrorMessage(""); setResendTimer(0); }}
                    className="text-indigo-600 hover:text-indigo-700 underline underline-offset-2"
                    data-testid="edit-email-hint"
                  >
                    check that your email is correct
                  </button>.
                </p>
              </form>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-6">
          Secured by <span className="font-medium text-slate-500">Phyber</span>
        </p>
      </div>
    </div>
  );
}
